import { useCallback, useEffect, useRef, useState, useContext } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  CircularProgress,
  Alert,
} from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import { ActivityCard } from "./ActivityCard";
import { formatDate, downloadPDF } from "../../../utils/exportUtils";
import AuthContext from "../../../context/AuthProvider";
import {
  AssessmentIcon,
  CheckInIcon,
  IncidentIcon,
  KudosIcon,
  MediaIcon,
  MedsIcon,
  NotesIcon,
  SkillIcon,
  QuranIcon,
  RecitationIcon,
  CustomIcon,
} from "../../../assets/svg/icons";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import API_URLS from "../../../constants/apiConstants";
import { useTranslation } from "react-i18next";

const ActivityFeed = ({ studentId }) => {
  const { t } = useTranslation();
  const axiosPrivate = useAxiosPrivate();
  const observerRef = useRef(null);
  const printRef = useRef(null); // wraps everything that should appear in the PDF
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [error, setError] = useState(null);
  const [activities, setActivities] = useState([]);
  const [activityType, setActivityType] = useState("all");
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 30; // matches the Flutter app's ActivitiesController._pageSize
  const { school, auth } = useContext(AuthContext);

  // ─── Date range filter ───────────────────────────────────────────────────
  // Empty string = no bound on that side. Sent to backend as start_date/end_date.
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [dateError, setDateError] = useState(null);

  // Which activity types are relevant depends on the nursery's type (e.g. a
  // Quran school vs a sports school have different activity types), so this
  // is fetched per-nursery instead of hardcoded.
  const [activityOptions, setActivityOptions] = useState([
    { value: "all", label: "studentDetails.activityFeed.allActivities" },
  ]);

  useEffect(() => {
    if (!school?.id) return;
    let cancelled = false;

    axiosPrivate
      .get(API_URLS.getActivityTypes(school.id))
      .then((response) => {
        if (cancelled) return;
        // Confirmed shape: a bare array of { name, image, url }, where
        // `url` (== `name`) is the activity_type code and `image` is a
        // server-relative icon path for that type.
        const raw = Array.isArray(response.data) ? response.data : [];

        const options = raw.map((item) => {
          const value = item.url ?? item.name;
          return {
            value,
            // Reuse existing studentDetails.activityFeed.<type> translations
            // where they exist; fall back to the raw type code otherwise.
            label: `studentDetails.activityFeed.${value}`,
            icon: item.image,
          };
        });

        setActivityOptions([
          { value: "all", label: "studentDetails.activityFeed.allActivities" },
          ...options,
        ]);
      })
      .catch((error) => {
        console.error("Failed to load activity types:", error);
        // Keep just "All Activities" so the page still works even if this
        // endpoint is unavailable.
      });

    return () => {
      cancelled = true;
    };
  }, [axiosPrivate, school?.id]);

  // Mirrors the Flutter app's CustomTitlesService: the feed can return
  // custom_types as bare integer IDs (rather than {id, title} objects), so
  // we cache id -> title for every tag-bearing activity type ('custom',
  // 'skill', 'subjectshomework' — same set activity_card.dart treats as
  // "isCustom") and use it to resolve IDs to real names in the card title.
  const [customTypesById, setCustomTypesById] = useState({});

  useEffect(() => {
    if (!school?.id) return;
    let cancelled = false;

    Promise.all(
      ["custom", "skill", "subjectshomework"].map((activityType) =>
        axiosPrivate
          .get(API_URLS.getCustomTypes(school.id, activityType))
          .then((response) => (Array.isArray(response.data) ? response.data : []))
          .catch((error) => {
            console.error(`Failed to load custom types for ${activityType}:`, error);
            return [];
          })
      )
    ).then((results) => {
      if (cancelled) return;
      const map = {};
      results.flat().forEach((ct) => {
        if (ct?.id != null) map[ct.id] = ct.title;
      });
      setCustomTypesById(map);
    });

    return () => {
      cancelled = true;
    };
  }, [axiosPrivate, school?.id]);

  // Mirrors the item-handling inside Flutter's parseImageList(): each entry
  // in an images list can be a plain string OR an object like
  // { image | url | file }. Normalizing here means ActivityCard always
  // receives plain strings, regardless of which shape the backend sends.
  const extractImageUrl = (item) => {
    if (item == null) return null;
    if (typeof item === "string") return item;
    if (typeof item === "object") return item.image ?? item.url ?? item.file ?? null;
    return String(item);
  };

  // The backend returns a flat, polymorphic activity shape:
  //   { activity_type, created_at, is_visible_to_staff_only, note, images, video, ... }
  // ActivityCard expects: { type, datetime, visible_staff_only, details: { ... PascalCase fields } }
  // NOTE: "media" was confirmed against a real payload. "skill", "observation",
  // "subjectshomework" and "custom" are mapped from the fields the backend confirmed
  // it accepts on create (note, file, custom_types, progress, skill_name, due_date) —
  // these are assumed to come back with the same field names on the feed GET response.
  // custom_types is assumed to be a list (either raw IDs or {id, title} objects — both
  // are handled in ActivityCard) since a nursery's tags come from the separate
  // /custom-types/?activity_type=... endpoint (see API_URLS.getCustomTypes).
  const normalizeActivity = (a) => {
    const base = {
      type: a.activity_type,
      datetime: a.created_at,
      visible_staff_only: a.is_visible_to_staff_only,
    };
    switch (a.activity_type) {
      case "media": {
        const images = (a.images ?? []).map(extractImageUrl).filter(Boolean);
        return {
          ...base,
          details: {
            Note: a.note,
            ActivityImage: images[0],
            ActivityImages: images,
            ActivityVideo: a.video,
          },
        };
      }
      case "skill":
        return {
          ...base,
          details: {
            Note: a.note,
            SkillName: a.skill_name,
            Progress: a.progress,
            File: a.file,
            CustomTypes: a.custom_types,
          },
        };
      case "observation":
        return {
          ...base,
          details: {
            Note: a.note,
            File: a.file,
          },
        };
      case "subjectshomework":
        return {
          ...base,
          details: {
            Note: a.note,
            File: a.file,
            CustomTypes: a.custom_types,
            DueDate: a.due_date,
          },
        };
      case "custom":
        // Confirmed against a real payload: custom activities can carry
        // images (array), a video, a file, a note and custom_types — the
        // same generic "attachments" shape as media, plus tags.
        return {
          ...base,
          details: {
            Note: a.note,
            ActivityImages: (a.images ?? []).map(extractImageUrl).filter(Boolean),
            ActivityVideo: a.video,
            File: a.file,
            CustomTypes: a.custom_types,
          },
        };
      case "achievement":
      case "kudos": {
        // Backend confirmed the current value is "achievement" (legacy
        // Flutter code called this "kudos" — kept as an alias in case an
        // older record still has the old value).
        const images = (a.images ?? []).map(extractImageUrl).filter(Boolean);
        return {
          ...base,
          type: "achievement",
          details: { Note: a.note, ActivityImages: images },
        };
      }
      case "incident": {
        const images = (a.images ?? []).map(extractImageUrl).filter(Boolean);
        return {
          ...base,
          details: { Note: a.note, ActivityImages: images },
        };
      }
      case "note": {
        const images = (a.images ?? []).map(extractImageUrl).filter(Boolean);
        return {
          ...base,
          details: { Note: a.note, ActivityImages: images },
        };
      }
      // Quran homework — backend confirmed the current value is "homework"
      // (legacy Flutter code called this "quran" — kept as an alias).
      case "homework":
      case "quran":
        return {
          ...base,
          type: "homework",
          details: {
            Note: a.note,
            RevisionNote: a.revision_note,
            NewSura: a.new_sura,
            NewAyaFrom: a.new_aya_from,
            NewAyaTo: a.new_aya_to,
            NewJuz: a.new_juz,
            NewJuzMul: a.new_juz_mul,
            NewSuraMul: a.new_sura_mul,
            RevisionSura: a.revision_sura,
            RevisionAyaFrom: a.revision_aya_from,
            RevisionAyaTo: a.revision_aya_to,
            RevisionJuz: a.revision_juz,
            RevisionJuzMul: a.revision_juz_mul,
            RevisionSuraMul: a.revision_sura_mul,
          },
        };
      case "recitation":
        return {
          ...base,
          details: {
            Note: a.note,
            RevisionNote: a.revision_note,
            NewSura: a.new_sura,
            NewAyaFrom: a.new_aya_from,
            NewAyaTo: a.new_aya_to,
            NewJuz: a.new_juz,
            NewJuzMul: a.new_juz_mul,
            NewSuraMul: a.new_sura_mul,
            RevisionSura: a.revision_sura,
            RevisionAyaFrom: a.revision_aya_from,
            RevisionAyaTo: a.revision_aya_to,
            RevisionJuz: a.revision_juz,
            RevisionJuzMul: a.revision_juz_mul,
            RevisionSuraMul: a.revision_sura_mul,
            MistakesTelawaMemorization: a.mistakes_telawa_memorization,
            MistakesTagweedMemorization: a.mistakes_tagweed_memorization,
            ScoreMemorization: a.score_memorization,
            MistakesTelawaRevision: a.mistakes_telawa_revision,
            MistakesTagweedRevision: a.mistakes_tagweed_revision,
            ScoreRevision: a.score_revision,
          },
        };
      case "quranassessment":
      case "assessment":
        return {
          ...base,
          type: "quranassessment",
          details: {
            Note: a.note,
            Audio: a.audio,
            SuraFrom: a.sura_from,
            SuraTo: a.sura_to,
            AyaFrom: a.aya_from,
            AyaTo: a.aya_to,
            JuzFrom: a.juz_from,
            JuzTo: a.juz_to,
            MistakesTelawa: a.mistakes_telawa,
            MistakesTagweed: a.mistakes_tagweed,
            Score: a.score,
          },
        };
      default:
        // Fall back to passing the raw object through as `details` so at least
        // a Note (if present) still renders instead of nothing.
        return { ...base, details: a };
    }
  };

  const fetchFeed = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const url = API_URLS.getFeed(school.id, studentId, activityType, {
        page: 1,
        pageSize: PAGE_SIZE,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
      });
      const response = await axiosPrivate.get(url);
      const data = (response.data.results || []).map(normalizeActivity);
      setActivities(data);
      setPage(1);
      // Mirrors the Flutter app: hasMore is inferred from a full page coming
      // back, not from a `next` field the feed endpoint isn't confirmed to send.
      setHasMore(data.length === PAGE_SIZE);
    } catch (error) {
      console.error("GET request error:", error);
      setError(t("studentDetails.activityFeed.error"));
    } finally {
      setLoading(false);
    }
  }, [activityType, axiosPrivate, studentId, school.id, startDate, endDate, t]);

  const loadMoreFeed = useCallback(async () => {
    if (loading || loadingMore || !hasMore) return;
    const nextPage = page + 1;
    try {
      setError(null);
      setLoadingMore(true);
      const url = API_URLS.getFeed(school.id, studentId, activityType, {
        page: nextPage,
        pageSize: PAGE_SIZE,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
      });
      const response = await axiosPrivate.get(url);
      const data = (response.data.results || []).map(normalizeActivity);
      setActivities((prev) => [...prev, ...data]);
      setPage(nextPage);
      setHasMore(data.length === PAGE_SIZE);
    } catch (error) {
      console.error("GET request error:", error);
      setError(t("studentDetails.activityFeed.error"));
    } finally {
      setLoadingMore(false);
    }
  }, [
    activityType,
    axiosPrivate,
    hasMore,
    loading,
    loadingMore,
    page,
    studentId,
    school.id,
    startDate,
    endDate,
    t,
  ]);

  // Effect to reset pagination when the activity type or date range changes
  useEffect(() => {
    setHasMore(false);
    setPage(1);
  }, [activityType, startDate, endDate]);

  useEffect(() => {
    fetchFeed();
  }, [fetchFeed]);

  useEffect(() => {
    if (!hasMore || loading || loadingMore) return;

    const target = observerRef.current;
    if (!target) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          loadMoreFeed();
        }
      },
      { root: null, threshold: 0 }
    );

    observer.observe(target);

    return () => {
      observer.unobserve(target);
      observer.disconnect();
    };
  }, [hasMore, loading, loadingMore, loadMoreFeed]);

  // ─── Date validation: "from" can't be after "to" ────────────────────────
  const handleStartDateChange = (value) => {
    setStartDate(value);
    if (value && endDate && value > endDate) {
      setDateError(t("studentDetails.activityFeed.dateRangeError"));
    } else {
      setDateError(null);
    }
  };

  const handleEndDateChange = (value) => {
    setEndDate(value);
    if (startDate && value && startDate > value) {
      setDateError(t("studentDetails.activityFeed.dateRangeError"));
    } else {
      setDateError(null);
    }
  };

  // ─── Print / export currently displayed (filtered) activities ──────────
  const handlePrint = async () => {
    if (!printRef.current || printing) return;
    try {
      setPrinting(true);
      const filenameDateBit =
        startDate || endDate
          ? `_${startDate || "start"}_to_${endDate || "end"}`
          : "";
      await downloadPDF(
        printRef.current,
        `activity_report${filenameDateBit}.pdf`
      );
    } catch (err) {
      console.error("PDF export error:", err);
      setError(t("studentDetails.activityFeed.printError"));
    } finally {
      setPrinting(false);
    }
  };

  // Mirrors the Flutter app's _resolveDisplayTitle: "custom", "skill" and
  // "subjectshomework" activities let staff pick zero or more custom_types
  // tags, so the card header shows those tag titles joined together instead
  // of a generic "Skill"/"Custom" label — falling back to the generic
  // translated label only when no tags were picked.
  const resolveDisplayTitle = (type, details) => {
    const isTagged =
      type === "custom" || type === "skill" || type === "subjectshomework";

    if (isTagged) {
      const types = details?.CustomTypes;
      if (types?.length > 0) {
        const labels = types.map((ct) => {
          if (typeof ct === "object") return ct.title ?? ct.name ?? ct.id;
          // Bare ID — resolve via the custom-types cache, same as Flutter's
          // CustomTitlesService.resolveIds. Falls back to the raw id only if
          // the cache hasn't loaded it (shouldn't normally happen).
          return customTypesById[ct] ?? ct;
        });
        return labels.join(", ");
      }
    }

    return t(`studentDetails.activityFeed.${type?.toLowerCase()}`, {
      defaultValue: type,
    });
  };

  const getIconByType = (type) => {
    const icons = {
      quran: <QuranIcon style={{ width: 32, height: 32 }} />,
      homework: <QuranIcon style={{ width: 32, height: 32 }} />,
      meds: <MedsIcon style={{ width: 32, height: 32 }} />,
      checkin: <CheckInIcon style={{ width: 32, height: 32 }} />,
      photo: <MediaIcon style={{ width: 32, height: 32 }} />,
      media: <MediaIcon style={{ width: 32, height: 32 }} />,
      skill: <SkillIcon style={{ width: 32, height: 32 }} />,
      incident: <IncidentIcon style={{ width: 32, height: 32 }} />,
      note: <NotesIcon style={{ width: 32, height: 32 }} />,
      kudos: <KudosIcon style={{ width: 32, height: 32 }} />,
      achievement: <KudosIcon style={{ width: 32, height: 32 }} />,
      assessment: <AssessmentIcon style={{ width: 32, height: 32 }} />,
      quranassessment: <AssessmentIcon style={{ width: 32, height: 32 }} />,
      recitation: <RecitationIcon style={{ width: 32, height: 32 }} />,
      custom: <CustomIcon style={{ width: 32, height: 32 }} />,
    };
    return icons[type] || <CustomIcon style={{ width: 32, height: 32 }} />;
  };

  return (
    <Box sx={{ maxWidth: 900, mx: "auto", p: 2 }}>
      <Box
        sx={{
          display: "flex",
          gap: 2,
          flexWrap: "wrap",
          mb: 1,
          justifySelf: "center",
          alignItems: "flex-start",
        }}
      >
        <FormControl sx={{ minWidth: 150 }}>
          <InputLabel>{t("studentDetails.activityFeed.activity")}</InputLabel>
          <Select
            value={activityType}
            label={t("studentDetails.activityFeed.activity")}
            onChange={(e) => setActivityType(e.target.value)}
            sx={{
              "& .MuiInputBase-input": {
                height: "0.5rem",
                paddingTop: "8px", // Adjust top padding
                paddingBottom: "8px", // Adjust bottom padding
              },
            }}
          >
            {activityOptions.map(({ value, label }) => (
              <MenuItem key={value} value={value}>
                {t(label, { defaultValue: value })}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          type="date"
          size="small"
          label={t("studentDetails.activityFeed.startDate")}
          value={startDate}
          onChange={(e) => handleStartDateChange(e.target.value)}
          InputLabelProps={{ shrink: true }}
          error={Boolean(dateError)}
          sx={{ minWidth: 150 }}
        />

        <TextField
          type="date"
          size="small"
          label={t("studentDetails.activityFeed.endDate")}
          value={endDate}
          onChange={(e) => handleEndDateChange(e.target.value)}
          InputLabelProps={{ shrink: true }}
          error={Boolean(dateError)}
          sx={{ minWidth: 150 }}
        />

        <Button
          variant="outlined"
          startIcon={printing ? <CircularProgress size={16} /> : <PrintIcon />}
          onClick={handlePrint}
          disabled={printing || loading || activities?.length === 0}
          sx={{ alignSelf: "center", height: 40 }}
        >
          {t("studentDetails.activityFeed.print")}
        </Button>
      </Box>

      {dateError && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {dateError}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ textAlign: "center", mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : activities?.length === 0 ? (
        <Alert severity="info">
          {t("studentDetails.activityFeed.noActivities")}
        </Alert>
      ) : (
        <>
          {/* Only this ref'd container is exported to PDF */}
          <Box ref={printRef}>
            {activities?.map((activity, index) => {
            const { type, datetime, visible_staff_only, details } = activity;
            return (
              <Card
                key={index}
                variant="outlined"
                sx={{ borderRadius: 3, mb: 2 }}
              >
                <CardContent sx={{ display: "flex", gap: 2 }}>
                  <Box>{getIconByType(type)}</Box>
                  <Box>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 600, textTransform: "capitalize" }}
                    >
                      {resolveDisplayTitle(type, details)}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 1 }}
                    >
                      {formatDate(datetime)}
                    </Typography>
                    <ActivityCard type={type} details={details} />
                    {visible_staff_only && (
                      <Typography
                        variant="body2"
                        fontStyle="italic"
                        fontWeight="bold"
                        color="text.secondary"
                        className="mt-3"
                      >
                        {t("studentDetails.activityFeed.visibleStaffOnly")}
                      </Typography>
                    )}
                  </Box>
                </CardContent>
              </Card>
            );
          })}
          </Box>

          {loadingMore && <Box sx={{ textAlign: "center", mt: 4 }}>
            <CircularProgress />
          </Box>}
          {hasMore && <Box ref={observerRef} sx={{ height: 1 }} />}
        </>
      )}
    </Box>
  );
};

export default ActivityFeed;